import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-purple-800 mb-3">{title}</h2>
    <div className="space-y-3 text-gray-700 text-[16px] leading-relaxed">{children}</div>
  </div>
);

const TermsPage = () => (
  <div className="min-h-screen bg-white">
    <SEOHead title="Terms of Service | Wanderloop" description="Terms and conditions for using Wanderloop." />
    <Header />
    <main className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-400 mb-10">Effective April 15, 2026</p>

      <Section title="Acceptance of Terms">
        <p>By creating an account or using Wanderloop, you agree to these terms. If you do not agree, please do not use the service.</p>
      </Section>

      <Section title="Description of Service">
        <p>Wanderloop provides curated urban walking routes, including route maps, points of interest, editorial descriptions, and community-contributed content. All walk information is provided for informational and recreational purposes only.</p>
      </Section>

      <Section title="Walk Accuracy and Safety">
        <p>Walk routes are provided for general guidance only. Trail conditions, business hours, road closures, and accessibility may change without notice. Wanderloop makes reasonable efforts to keep information current but does not guarantee accuracy.</p>
        <p>You walk at your own risk. Wanderloop is not responsible for injuries, property damage, or any other losses that occur while following a walk route. Always use your own judgment, check local conditions before setting out, and follow posted signs and regulations.</p>
      </Section>

      <Section title="User Accounts">
        <p>You are responsible for maintaining the security of your account credentials. Do not share your password. One account per person. Accounts created by automated means may be suspended without notice.</p>
        <p>You must be at least 13 years old to create an account.</p>
      </Section>

      <Section title="Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Scrape, crawl, or systematically download Wanderloop content</li>
          <li>Use automated tools to access the service</li>
          <li>Reproduce, redistribute, or commercially use walk descriptions, editorial content, or images without written permission</li>
          <li>Submit false, misleading, or harmful content through the community feedback system</li>
          <li>Attempt to access other users' accounts or data</li>
        </ul>
      </Section>

      <Section title="Community Submissions">
        <p>When you submit walk improvements, corrections, or feedback through Wanderloop, you grant Wanderloop a non-exclusive, royalty-free, worldwide license to use, edit, publish, or decline your submission at our discretion.</p>
        <p>You retain ownership of your original content. We may edit submissions for clarity, accuracy, or tone. We are not obligated to use or respond to every submission.</p>
      </Section>

      <Section title="Wanderloop+ Subscriptions">
        <p>Wanderloop+ is a paid subscription that provides additional features. Subscriptions are billed through Stripe on a monthly or annual basis depending on the plan selected.</p>
        <p>You may cancel your subscription at any time from your account settings or the Stripe customer portal. Cancellation takes effect at the end of the current billing period. We do not offer refunds for partial billing periods.</p>
      </Section>

      <Section title="Intellectual Property">
        <p>Walk routes, editorial descriptions, curated content, the Wanderloop brand, and site design are owned by Wanderloop, LLC. Photos sourced from Unsplash are used under the Unsplash License and attributed to their respective photographers.</p>
        <p>You may share links to walk pages freely. You may not copy, reproduce, or republish walk descriptions or editorial content without written permission.</p>
      </Section>

      <Section title="Limitation of Liability">
        <p>To the maximum extent permitted by law, Wanderloop and its officers, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service, including but not limited to injuries, property damage, or losses incurred while following walk routes.</p>
        <p>Our total liability for any claim arising from the service is limited to the amount you paid for Wanderloop+ in the 12 months preceding the claim, or $50, whichever is greater.</p>
      </Section>

      <Section title="Governing Law">
        <p>These terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of Los Angeles County, California.</p>
      </Section>

      <Section title="Changes to These Terms">
        <p>We may update these terms from time to time. When we make material changes, we will notify you by email or by posting a notice on the site. Your continued use of Wanderloop after changes take effect constitutes acceptance of the updated terms.</p>
      </Section>

      <Section title="Contact">
        <p>For questions about these terms, email <a href="mailto:hello@wanderloop.app" className="text-purple-700 underline">hello@wanderloop.app</a>.</p>
      </Section>
    </main>
    <Footer />
  </div>
);

export default TermsPage;
